import type { AxiosInstance } from 'axios';

import type { MaterialKind, PartUnits } from '@accio3d/shared/domain';

export type BatchUploadFileType = 'drawing' | 'csv' | 'spec' | 'other';

export interface BatchUploadFileRequest {
  fileName: string;
  contentType: string;
  fileType: BatchUploadFileType;
}

export interface BatchUploadFileResult {
  fileName: string;
  fileType: BatchUploadFileType;
  uploadUrl: string;
  blobUrl: string;
  expiresAt: string;
}

export interface BatchUploadResult {
  uploads: BatchUploadFileResult[];
}

export interface BulkCreatePartPayload {
  name: string;
  partNumber: string;
  description?: string | null;
  fileUrls: string[];
  csvUrl?: string | null;
  quantity: number;
  units: PartUnits;
  /** Omitted for drawing-only parts (CAD reconstruction requests). */
  materialKind?: MaterialKind;
  castorMaterialId?: number;
  castorPrintedCategory?: string;
  castorPrintedSubcategory?: string;
  castorPrintedMaterialId?: string;
  currency?: string;
  docsSpec?: boolean;
  docsDrawing2d?: boolean;
  docsCad3d?: boolean;
  volume?: number | null;
  surfaceArea?: number | null;
  dimensions?: Record<string, unknown> | null;
  targetPrice?: number | null;
  annualVolume?: number | null;
}

export type PartUploadChoice = Pick<
  BulkCreatePartPayload,
  | 'quantity'
  | 'units'
  | 'materialKind'
  | 'castorMaterialId'
  | 'castorPrintedCategory'
  | 'castorPrintedSubcategory'
  | 'castorPrintedMaterialId'
>;

export interface CreatedPart {
  id: string;
  name: string;
  partNumber: string;
  fileUrls?: string[] | null;
}

export interface PartUploadService {
  getBatchUploadUrls: (files: BatchUploadFileRequest[]) => Promise<BatchUploadResult>;
  createPartsFromBlobs: (parts: BulkCreatePartPayload[]) => Promise<CreatedPart[]>;
  appendPartFiles: (
    partId: string,
    payload: {
      fileUrls: string[];
      docsDrawing2d?: boolean;
      docsSpec?: boolean;
    },
  ) => Promise<{ signedFileUrls: string[] }>;
  removePartFiles: (partId: string, payload: { fileUrls: string[] }) => Promise<void>;
  extractDrawingRequirements: (payload: {
    imageUrls: string[];
    fileName?: string;
  }) => Promise<ExtractedDrawingRequirements>;
}

export interface ExtractedDrawingRequirements {
  preferredProcess: string | null;
  preferredMaterial: string | null;
  tightestToleranceMm: number | null;
  finishingTreatment: string | null;
  surfaceRoughness: string | null;
  inserts: string | null;
  stringentRequirements: string[];
  notesForSupplier: string | null;
}

export function createPartUploadService(client: AxiosInstance): PartUploadService {
  return {
    getBatchUploadUrls: async (files: BatchUploadFileRequest[]) => {
      const { data } = await client.post<BatchUploadResult>('/purchasers/parts/uploads/batch', {
        files,
      });
      return data;
    },
    createPartsFromBlobs: async (parts: BulkCreatePartPayload[]) => {
      const { data } = await client.post<CreatedPart[]>('/parts/bulk', { parts });
      return data;
    },
    appendPartFiles: async (partId, payload) => {
      const { data } = await client.post<{ signedFileUrls: string[] }>(
        `/parts/${partId}/files`,
        payload,
      );
      return data;
    },
    removePartFiles: async (partId, payload) => {
      await client.delete(`/parts/${partId}/files`, { data: payload });
    },
    extractDrawingRequirements: async (payload) => {
      const { data } = await client.post<ExtractedDrawingRequirements>(
        '/purchasers/extract-drawing-requirements',
        payload,
      );
      return data;
    },
  };
}

export async function uploadFileToBlob(
  uploadUrl: string,
  file: File | Blob,
  contentType: string,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    body: file,
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
      'x-ms-blob-content-type': contentType,
    },
  });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Blob upload failed (${response.status} ${response.statusText})${body ? `: ${body}` : ''}`,
    );
  }
}
